/////////////////////////////////////////////////////////////////////////////
// Shared vocabulary for the two registration forms.                       //
/////////////////////////////////////////////////////////////////////////////
//
// Source of truth: "PFS ECOSYSTEM MARKETPLACE — Registration Forms /
// Service Providers & Food Businesses" (the vetting form Vivian shared,
// `docs/source-docs/pfs-registration-forms-2026-09.md`).
//
// That document opens with the reason this file exists:
//
//   "Note: Designed for a bilingual digital onboarding experience. The same
//    service taxonomy is used for provider capabilities and food-business
//    needs to enable accurate marketplace matching."
//
// So the service list is NOT duplicated between configListing.js (Form 1
// §03 "Services & Expertise") and configUser.js (Form 2 §04 "Support
// Needed"). Both import the same leaves from here, which is what makes
// "provider offers X" and "business needs X" comparable at all. Two
// hand-maintained lists drift within one release and silently stop matching
// — the previous implementation had exactly that, a 37-item
// `areasOfExpertise` on one side and a 30-item `businessNeeds` on the other
// with no shared values.
//
// The same reasoning applies to the food sector list, which Form 1 §04
// ("Which food sectors have you worked with?") and Form 2 §03 ("Which value
// chain(s) best describe your core business?") state identically.
//
// Option VALUES are slugs, not display strings: a label edit must not orphan
// stored data, and they read acceptably in a `?pub_serviceAreas=` URL.

/////////////////////////////
// Group headings          //
/////////////////////////////
// The two forms bucket the same leaves differently — Form 1 uses six
// headings, Form 2 merges them into four. Both sets are declared here and
// each leaf names its heading on each side, rather than the lists being
// ordered by accident.

export const PROVIDER_SERVICE_GROUPS = {
  foodSafety: 'Food Safety, Quality & Regulatory',
  productProcessing: 'Product & Processing',
  businessGrowth: 'Business & Market Growth',
  financeInvestment: 'Finance & Investment',
  operationsInfrastructure: 'Operations & Infrastructure',
  peopleOrganizations: 'People & Organizations',
};

export const BUSINESS_SERVICE_GROUPS = {
  foodSafety: 'Food Safety, Quality & Regulatory',
  productProcessing: 'Product & Processing',
  businessFinanceMarkets: 'Business, Finance & Markets',
  operationsInfrastructurePeople: 'Operations, Infrastructure & People',
};

/////////////////////////////
// The shared service leaves//
/////////////////////////////
/**
 * One entry per service, in the order Form 1 lists them.
 *
 * - `option`         stored value (slug)
 * - `label`          Form 1 wording
 * - `providerGroup`  heading in Form 1 §03; null = not offered by providers
 * - `businessGroup`  heading in Form 2 §04; null = not requestable by a
 *                    food business, so it is dropped from that form
 * - `businessLabel`  Form 2 wording where the two forms word it differently
 */
export const SERVICE_TAXONOMY = [
  // --- Food Safety, Quality & Regulatory ---------------------------------
  {
    option: 'food-safety-quality-systems',
    label: 'Food Safety & Quality Systems',
    providerGroup: 'foodSafety',
    businessGroup: 'foodSafety',
  },
  {
    option: 'training-and-auditing',
    label: 'Training & Auditing',
    providerGroup: 'foodSafety',
    businessGroup: 'foodSafety',
  },
  {
    option: 'laboratory-and-testing-services',
    label: 'Laboratory & Testing Services',
    providerGroup: 'foodSafety',
    businessGroup: 'foodSafety',
  },
  {
    option: 'certification',
    label: 'Certification',
    providerGroup: 'foodSafety',
    businessGroup: 'foodSafety',
    // Form 2 spells the same leaf out for a food-business audience, which
    // would not know what "Certification" covers on its own.
    businessLabel: 'HACCP, Organic, GFSI Certification',
  },
  {
    option: 'regulatory-and-legal-compliance',
    label: 'Regulatory & Legal Compliance',
    providerGroup: 'foodSafety',
    businessGroup: 'foodSafety',
  },

  // --- Product & Processing ----------------------------------------------
  {
    option: 'product-development-and-reformulation',
    label: 'Product Development & Reformulation',
    providerGroup: 'productProcessing',
    businessGroup: 'productProcessing',
  },
  {
    option: 'nutrition-and-fortification',
    label: 'Nutrition & Fortification',
    providerGroup: 'productProcessing',
    businessGroup: 'productProcessing',
  },
  {
    option: 'process-engineering',
    label: 'Process Engineering',
    providerGroup: 'productProcessing',
    businessGroup: 'productProcessing',
  },
  {
    option: 'equipment-supply',
    label: 'Equipment Supply',
    providerGroup: 'productProcessing',
    businessGroup: 'productProcessing',
  },
  {
    option: 'equipment-installation-and-maintenance',
    label: 'Equipment Installation & Maintenance',
    providerGroup: 'productProcessing',
    businessGroup: 'productProcessing',
  },
  {
    option: 'ingredients-and-processing-aids',
    label: 'Ingredients & Processing Aids',
    providerGroup: 'productProcessing',
    businessGroup: 'productProcessing',
  },
  {
    option: 'packaging-solutions',
    label: 'Packaging Solutions',
    providerGroup: 'productProcessing',
    businessGroup: 'productProcessing',
  },

  // --- Business & Market Growth ------------------------------------------
  // Form 2 folds this group and "Finance & Investment" into a single
  // "Business, Finance & Markets" heading.
  {
    option: 'business-strategy-and-management-consulting',
    label: 'Business Strategy & Management Consulting',
    providerGroup: 'businessGrowth',
    businessGroup: 'businessFinanceMarkets',
  },
  {
    option: 'business-development-and-financial-planning',
    label: 'Business Development & Financial Planning',
    providerGroup: 'businessGrowth',
    businessGroup: 'businessFinanceMarkets',
  },
  {
    option: 'marketing-branding-and-design',
    label: 'Marketing, Branding & Design',
    providerGroup: 'businessGrowth',
    businessGroup: 'businessFinanceMarkets',
  },
  {
    option: 'market-research',
    label: 'Market Research',
    providerGroup: 'businessGrowth',
    businessGroup: 'businessFinanceMarkets',
  },
  {
    option: 'export-and-market-access',
    label: 'Export & Market Access',
    providerGroup: 'businessGrowth',
    businessGroup: 'businessFinanceMarkets',
  },

  // --- Finance & Investment ----------------------------------------------
  {
    option: 'investment-and-financing',
    label: 'Investment & Financing',
    providerGroup: 'financeInvestment',
    businessGroup: 'businessFinanceMarkets',
  },
  {
    option: 'financial-advisory',
    label: 'Financial Advisory',
    providerGroup: 'financeInvestment',
    businessGroup: 'businessFinanceMarkets',
  },
  {
    option: 'tax-advisory',
    label: 'Tax Advisory',
    providerGroup: 'financeInvestment',
    businessGroup: 'businessFinanceMarkets',
  },

  // --- Operations & Infrastructure ---------------------------------------
  // Form 2 folds this group and "People & Organizations" into a single
  // "Operations, Infrastructure & People" heading.
  {
    option: 'supply-chain-procurement-and-raw-material-sourcing',
    label: 'Supply Chain, Procurement & Raw Material Sourcing',
    providerGroup: 'operationsInfrastructure',
    businessGroup: 'operationsInfrastructurePeople',
  },
  {
    option: 'logistics-and-transportation',
    label: 'Logistics & Transportation',
    providerGroup: 'operationsInfrastructure',
    businessGroup: 'operationsInfrastructurePeople',
  },
  {
    option: 'renewable-energy-and-energy-efficiency',
    label: 'Renewable Energy & Energy Efficiency',
    providerGroup: 'operationsInfrastructure',
    businessGroup: 'operationsInfrastructurePeople',
  },
  {
    option: 'waste-and-environmental-management',
    label: 'Waste & Environmental Management',
    providerGroup: 'operationsInfrastructure',
    businessGroup: 'operationsInfrastructurePeople',
  },
  {
    option: 'digital-transformation-and-erp',
    label: 'Digital Transformation & ERP',
    providerGroup: 'operationsInfrastructure',
    businessGroup: 'operationsInfrastructurePeople',
  },

  // --- People & Organizations --------------------------------------------
  {
    option: 'recruitment-and-talent',
    label: 'Recruitment & Talent',
    providerGroup: 'peopleOrganizations',
    businessGroup: 'operationsInfrastructurePeople',
  },
  {
    option: 'leadership-and-human-capital-development',
    label: 'Leadership & Human Capital Development',
    providerGroup: 'peopleOrganizations',
    businessGroup: 'operationsInfrastructurePeople',
  },
  {
    option: 'development-nonprofit-services',
    label: 'Development / Nonprofit Services',
    providerGroup: 'peopleOrganizations',
    // Form 2 omits this leaf: it is a service a provider offers TO the
    // development sector, not support a food business asks for. Left out
    // rather than silently added, so the rendered form matches the document.
    businessGroup: null,
  },
  {
    option: 'other',
    label: 'Other',
    providerGroup: 'peopleOrganizations',
    businessGroup: 'operationsInfrastructurePeople',
  },
];

/**
 * The taxonomy as `enumOptions` for one side of the marketplace.
 *
 * Carries `group` through to the field so FieldCheckboxGroup can render the
 * document's headings instead of one undifferentiated 29-checkbox list.
 *
 * @param {'provider'|'business'} audience
 * @returns {Array<{option: string, label: string, group: string}>}
 */
export const serviceOptionsFor = audience => {
  const groupKey = audience === 'business' ? 'businessGroup' : 'providerGroup';
  const groupLabels =
    audience === 'business' ? BUSINESS_SERVICE_GROUPS : PROVIDER_SERVICE_GROUPS;

  return SERVICE_TAXONOMY.filter(entry => !!entry[groupKey]).map(entry => ({
    option: entry.option,
    label: (audience === 'business' && entry.businessLabel) || entry.label,
    group: groupLabels[entry[groupKey]],
  }));
};

/////////////////////////////
// Food sectors            //
/////////////////////////////
// Form 1 §04 "Food Industry Experience" and Form 2 §03 "Products & Markets"
// list these identically — the provider says which they have worked with,
// the business says which describe its core activity, and the match is the
// intersection. Same argument as the service taxonomy: one list.

export const FOOD_SECTORS = [
  { option: 'grains-and-milling', label: 'Grains & Milling' },
  { option: 'dairy', label: 'Dairy' },
  { option: 'bakery-and-snacks', label: 'Bakery & Snacks' },
  { option: 'beverages', label: 'Beverages' },
  { option: 'edible-oils-and-oilseeds', label: 'Edible Oils & Oilseeds' },
  { option: 'fruits-and-vegetables', label: 'Fruits & Vegetables' },
  { option: 'meat-and-poultry', label: 'Meat & Poultry' },
  { option: 'fisheries', label: 'Fisheries' },
  { option: 'animal-feed', label: 'Animal Feed' },
  { option: 'cocoa-and-coffee', label: 'Cocoa & Coffee' },
  { option: 'spices', label: 'Spices' },
  { option: 'nuts-and-seeds', label: 'Nuts & Seeds' },
  { option: 'ready-to-eat-foods', label: 'Ready-to-Eat Foods' },
  { option: 'restaurants-and-food-service', label: 'Restaurants & Food Service' },
  { option: 'retail', label: 'Retail' },
  { option: 'other', label: 'Other' },
];

/////////////////////////////
// Shared banded answers   //
/////////////////////////////
// Both forms ask "Number of employees" with the same five bands, so the band
// list is shared even though the two fields are worded differently
// ("Number of full-time employees" on Form 1 §06, "Number of employees" on
// Form 2 §02).

export const EMPLOYEE_BANDS = [
  { option: '1-9', label: '1–9' },
  { option: '10-20', label: '10–20' },
  { option: '21-50', label: '21–50' },
  { option: '51-100', label: '51–100' },
  { option: '100-plus', label: '100+' },
];

/** Form 1 §06 "Approximately how many food businesses have you served?" */
export const CLIENTS_SERVED_BANDS = [
  { option: 'fewer-than-10', label: 'Fewer than 10' },
  { option: '10-20', label: '10–20' },
  { option: '21-50', label: '21–50' },
  { option: '51-100', label: '51–100' },
  { option: '100-plus', label: '100+' },
];

/** Form 1 §06 "Years of relevant experience" */
export const EXPERIENCE_BANDS = [
  { option: 'less-than-3', label: 'Less than 3' },
  { option: '3-5', label: '3–5' },
  { option: '6-10', label: '6–10' },
  { option: '11-20', label: '11–20' },
  { option: '20-plus', label: '20+' },
];

/**
 * Form 2 §05 "Business Growth" — the same five bands are used for both the
 * current and the target revenue question.
 */
export const REVENUE_BANDS = [
  { option: 'under-500k', label: '< USD 500,000' },
  { option: '500k-1m', label: 'USD 500,000–999,999' },
  { option: '1m-5m', label: 'USD 1–5 million' },
  { option: '5m-20m', label: 'USD 5–20 million' },
  { option: 'over-20m', label: '> USD 20 million' },
];

/** Form 1 §05 / Form 2 (support delivery) — "Service delivery". */
export const SERVICE_DELIVERY_MODES = [
  { option: 'on-site', label: 'On-site' },
  { option: 'remote', label: 'Remote' },
  { option: 'both', label: 'Both' },
];
